import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiV1Url } from './config/api';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar, BarDatum } from '@nivo/bar';
import { Bar } from 'react-chartjs-2';
import DetailInfo from './components/LabList/DetailInfo';
import Certificates from './components/Certificates/Certificates';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Title } from 'chart.js';
import { LabExam } from './LabExam';
import { Certif } from './CertificatesPage';
import Header from './components/Header/Header';
import './styles/Examination.css';
import BarChartLabExam from './components/LabList/BarChartLabExam';
import BarChartDynamics from './components/Certificates/BarChartDynamics';
import DeclarationsTable from './components/Declarations/DeclarationsTable';
import DeclarationsDynamics from './components/Declarations/DeclarationsDynamics';
import { Declaration, DeclarationBasis } from './types/declaration';
import { DECLARATION_BASES, getDeclarationDetailPath, getLegalBasis } from './utils/declarationUtils';
import { getPdfButtonLabel, useRegistryPdfExport } from './hooks/useRegistryPdfExport';
import { buildExaminationDocument } from './utils/examinationPdfUtils';

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Title);

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const EXAM_RESULT_COLORS = {
  resultColors: {
    'Соответствует': 'rgba(126, 178, 70, 1)',
    'Соответствует НД': 'rgba(126, 178, 70, 1)',
    'Не соответствует (фальсификат)': 'rgba(237, 93, 98, 1)',
    'Не соответствует НД (фальсификат)': 'rgba(237, 93, 98, 1)',
    'Не соответствует (брак)': 'rgba(255, 166, 0, 1)',
    'Не соответствует НД (брак)': 'rgba(255, 166, 0, 1)',
    'Нет данных': 'rgba(196, 196, 196, 1)',
  },
  base: [
    'rgba(196, 196, 196, 1)',
    'rgba(237, 93, 98, 1)',
    'rgba(126, 178, 70, 1)',
    'rgba(255, 166, 0, 1)',
  ],
  dimmed: [
    'rgba(126, 178, 70, 0.3)',
    'rgba(237, 93, 98, 0.3)',
    'rgba(255, 166, 0, 0.3)',
    'rgba(255, 166, 0, 0.3)',
  ]
};

const APTS_COMMENT_COLORS = {
  base: [
    'rgba(126, 178, 70, 1)',
    'rgba(237, 93, 98, 1)',
    'rgba(255, 166, 0, 1)',
  ],
  dimmed: [
    'rgba(75, 192, 192, 0.2)',
    'rgba(255, 99, 132, 0.2)',
    'rgba(255, 206, 86, 0.2)',
  ]
};

const CERT_STATUS_COLORS = {
  base: [
    'rgba(126, 178, 70, 1)',
    'rgba(237, 93, 98, 1)',
    'rgba(247, 0, 150, 1)',
    'rgba(0, 0, 0, 1)',
    'rgba(255, 166, 0, 1)',
  ],
  dimmed: [
    'rgba(34, 197, 94, 0.2)',
    'rgba(239, 68, 68, 0.2)',
    'rgba(168, 85, 247, 0.2)',
    'rgba(59, 130, 246, 0.2)',
    'rgba(249, 115, 22, 0.2)',
  ]
};

const Examination = () => {
  const query = useQuery();
  const inn = query.get('inn') || '';
  const name = query.get('name') || '';
  const navigate = useNavigate();

  const [labList, setLabList] = useState<LabExam[]>([]);
  const [certList, setCertList] = useState<Certif[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [voluntaryList, setVoluntaryList] = useState<Certif[]>([]);
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  const [declarationsLoading, setDeclarationsLoading] = useState(false);
  const [declarationsError, setDeclarationsError] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { pdfProgress, startPdfExport, pdfReport } = useRegistryPdfExport();

  useEffect(() => {
    setLoading(true);
    const access = localStorage.getItem('access');
    Promise.all([
      axios.get(getApiV1Url('LAB_EXAMS_LIST'), {
        headers: access ? { Authorization: `Bearer ${access}` } : {},
        timeout: 30000
      }),
      axios.get(getApiV1Url('CERTIFICATES'), {
        headers: access ? { Authorization: `Bearer ${access}` } : {},
        timeout: 30000
      }),
      axios.get(getApiV1Url('VOLUNTARY_CERTIFICATES'), {
        headers: access ? { Authorization: `Bearer ${access}` } : {},
        timeout: 30000
      }),
      axios.get(getApiV1Url('COMPANIES'), {
        headers: access ? { Authorization: `Bearer ${access}` } : {},
        timeout: 30000
      })
    ]).then(([labs, certs, voluntary, companiesResp]) => {
      setLabList(Array.isArray(labs.data) ? labs.data : []);

      setCertList(certs.data);
      setCompanies(Array.isArray(companiesResp.data) ? companiesResp.data : []);
      const voluntaryMapped: Certif[] = (Array.isArray(voluntary.data) ? voluntary.data : []).map((v: any) => ({
        cert_reg_number: v.certificate_number || '',
        cert_status: v.certificate_validity_period || '',
        comment_apts: v.apts_comment || '',
        cert_reg_data: v.certificate_registration_date || '',
        cert_finish_data: v.certificate_expiry_date || '',
        cert_schema: v.sds_name || '',
        product_fullname: v.product || '',
        regulatory_documentation: v.regulatory_document || '',
        manufacture_inn: v.inn_unp ? String(v.inn_unp) : '',
        manufacture_fullname: v.manufacturer || '',
        os_name: v.os_name || '',
        os_number: v.os_code || '',
        lan_name_1: v.laboratory_name || '',
        lab_number_1: (v.accreditation_certificate || '').toString().trim(),
        applicant_adres: v.applicant_adres || '',
        os_address: v.os_address || '',
      } as any)) as unknown as Certif[];
      setVoluntaryList(voluntaryMapped);
      setLoading(false);
    }).catch((err: any) => {
      if (err.code === 'ECONNABORTED') {
        console.error('Превышено время ожидания ответа от сервера');
      } else {
        console.error('Ошибка загрузки:', err);
      }
      setLoading(false);
    });
  }, []);

  const filteredLabs = useMemo(() => {
    if (!inn && !name) return [];
    return labList.filter((item) => {
      // Если ИНН не равен "нет данных", то фильтруем по ИНН(снизу дублируется)
      if (inn && inn !== 'нет данных' && item.inn_company === inn) {
        return true;
      }
      // Если ИНН равен "нет данных" или отсутствует, то фильтруем по названию(снизу дублируется)
      if (name && (inn === 'нет данных' || !inn) && item.name_company === name) {
        return true;
      }
      // Если есть и ИНН, и название, то используем оба критерия(снизу дублируется)
      if (inn && name && inn !== 'нет данных' &&
          item.inn_company === inn && item.name_company === name) {
        return true;
      }
      return false;
    });
  }, [labList, inn, name]);

  const filteredWeldedJointLabs = useMemo(
    () => filteredLabs.filter(item => item.welding_joint_type?.trim()),
    [filteredLabs]
  );
  const filteredRegularLabs = useMemo(
    () => filteredLabs.filter(item => !item.welding_joint_type?.trim()),
    [filteredLabs]
  );
  const weldedJointTypeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    filteredWeldedJointLabs.forEach(item => {
      const type = item.welding_joint_type?.trim();
      if (type) counts.set(type, (counts.get(type) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0], 'ru'));
  }, [filteredWeldedJointLabs]);

  const filteredCerts = useMemo(() => {
    if (!inn && !name) return [];
    return certList.filter((item) => {
      if (inn && inn !== 'нет данных' && item.manufacture_inn === inn) {
        return true;
      }
      if (name && (inn === 'нет данных' || !inn) && item.manufacture_fullname === name) {
        return true;
      }
      if (inn && name && inn !== 'нет данных' &&
          item.manufacture_inn === inn && item.manufacture_fullname === name) {
        return true;
      }
      return false;
    });
  }, [certList, inn, name]);

  const filteredVoluntaryCerts = useMemo(() => {
    if (!inn && !name) return [];
    return voluntaryList.filter((item) => {
      if (inn && inn !== 'нет данных' && item.manufacture_inn === inn) {
        return true;
      }
      if (name && (inn === 'нет данных' || !inn) && item.manufacture_fullname === name) {
        return true;
      }
      if (inn && name && inn !== 'нет данных' &&
          item.manufacture_inn === inn && item.manufacture_fullname === name) {
        return true;
      }
      return false;
    });
  }, [voluntaryList, inn, name]);

  // Восстановление недостающих ИНН/наименования по всем доступным данным
  const displayInn = useMemo(() => {
    if (inn) return inn;
    if (!name) return '';
    const normName = String(name).trim().toLowerCase();

    // 1) COMPANIES по названию
    const company = companies.find(
      (c: any) => String(c.name || '').trim().toLowerCase() === normName
    );
    if (company?.inn) return String(company.inn);

    // 2) CERTIFICATES по manufacture_fullname
    const certByManName = certList.find(
      (c) => String(c.manufacture_fullname || '').trim().toLowerCase() === normName
    );
    if (certByManName?.manufacture_inn) return String(certByManName.manufacture_inn);

    // 3) CERTIFICATES по applicant_shortname
    const certByApplicantShort = certList.find(
      (c) => String(c.applicant_shortname || '').trim().toLowerCase() === normName
    );
    if (certByApplicantShort?.applicant_inn) return String(certByApplicantShort.applicant_inn);

    // 4) Лабораторные испытания по названию компании
    const labByName = labList.find(
      (l) => String(l.name_company || '').trim().toLowerCase() === normName
    );
    if (labByName?.inn_company) return String(labByName.inn_company);

    return '';
  }, [inn, name, companies, certList, labList]);

  // Декларации грузятся отдельно: если запрос упадёт, остальная проверка продолжает работать
  useEffect(() => {
    if (loading) return;
    const manufacturerInn = String(displayInn || '').trim();
    if (!manufacturerInn || manufacturerInn === 'нет данных') {
      setDeclarations([]);
      return;
    }
    let cancelled = false;
    setDeclarationsLoading(true);
    setDeclarationsError(false);
    const access = localStorage.getItem('access');
    axios.get(getApiV1Url('DECLARATIONS'), {
      params: { manufacturer_inn: manufacturerInn },
      headers: access ? { Authorization: `Bearer ${access}` } : {},
      timeout: 30000
    }).then(response => {
      if (!cancelled) setDeclarations(Array.isArray(response.data) ? response.data : []);
    }).catch((err: any) => {
      console.error('Ошибка загрузки деклараций:', err);
      if (!cancelled) {
        setDeclarations([]);
        setDeclarationsError(true);
      }
    }).finally(() => {
      if (!cancelled) setDeclarationsLoading(false);
    });
    return () => { cancelled = true; };
  }, [loading, displayInn]);

  // Декларации изготовителя, разделённые по ПП 2425 и ПП 353
  const declarationsByBasis = useMemo(() => {
    return DECLARATION_BASES.map(basis => ({
      basis,
      basisDeclarations: declarations.filter(declaration => (declaration.legal_basis || '').trim() === getLegalBasis(basis)),
    }));
  }, [declarations]);

  const declarationGroups = declarationsByBasis.filter(group => group.basisDeclarations.length > 0);
  const hasDeclarationData = declarationGroups.length > 0;

  const handleOpenDeclarationDetails = useCallback((basis: DeclarationBasis, declaration: Declaration) => {
    navigate(getDeclarationDetailPath(basis, declaration.reg_number), { state: { declaration } });
  }, [navigate]);

  // Динамика деклараций выводится сразу после динамики сертификатов
  const renderDeclarationDynamics = (style: React.CSSProperties) => {
    return declarationGroups.map(({ basis, basisDeclarations }) => (
      <div key={basis} className="bar-dynamics-container" style={{ ...style, height: 'auto' }}>
        <h3>Динамика деклараций {getLegalBasis(basis)} по годам</h3>
        <DeclarationsDynamics declarations={basisDeclarations} height={400} />
      </div>
    ));
  };

  const displayName = useMemo(() => {
    if (name) return name;
    if (!inn || inn === 'нет данных') return '';
    const normInn = String(inn).trim();

    // 1) COMPANIES по ИНН
    const company = companies.find(
      (c: any) => String(c.inn || '').trim() === normInn
    );
    if (company?.name) return String(company.name);

    // 2) CERTIFICATES по manufacture_inn
    const certByManInn = certList.find(
      (c) => String(c.manufacture_inn || '').trim() === normInn
    );
    if (certByManInn?.manufacture_fullname) return String(certByManInn.manufacture_fullname);

    // 3) CERTIFICATES по applicant_inn (applicant_shortname)
    const certByApplicantInn = certList.find(
      (c) => String(c.applicant_inn || '').trim() === normInn
    );
    if (certByApplicantInn?.applicant_shortname) return String(certByApplicantInn.applicant_shortname);

    // 4) Лабораторные испытания по inn_company
    const labByInn = labList.find(
      (l) => String(l.inn_company || '').trim() === normInn
    );
    if (labByInn?.name_company) return String(labByInn.name_company);

    return '';
  }, [inn, name, companies, certList, labList]);

  // Получение адреса из разных источников
  const organizationAddress = useMemo(() => {
    // 1) Сначала поиск в справочнике производителей (COMPANIES),
    if (companies.length > 0) {
      if (inn && inn !== 'нет данных') {
        const byInn = companies.find((c: any) => String(c.inn || '').trim() === String(inn).trim());
        if (byInn && byInn.address) {
          return byInn.address as string;
        }
      }
      if (name) {
        const byName = companies.find(
          (c: any) => String(c.name || '').trim().toLowerCase() === String(name).trim().toLowerCase()
        );
        if (byName && byName.address) {
          return byName.address as string;
        }
      }
    }

    // 2) Потом лабораторные испытания
    if (filteredLabs.length > 0 && filteredLabs[0].address_company) {
      return filteredLabs[0].address_company;
    }

    // 3) Потом обязательные сертификаты
    if (filteredCerts.length > 0 && filteredCerts[0].applicant_adres) {
      return filteredCerts[0].applicant_adres;
    }

    // 4) Потом добровольные сертификаты
    if (filteredVoluntaryCerts.length > 0) {
      const voluntary = filteredVoluntaryCerts[0] as any;
      if (voluntary.os_address) {
        return voluntary.os_address;
      }
      if (voluntary.applicant_adres) {
        return voluntary.applicant_adres;
      }
    }
    return 'Нет данных';
  }, [companies, filteredLabs, filteredCerts, filteredVoluntaryCerts, inn, name]);

  const handleDownload = () => startPdfExport(() => buildExaminationDocument({
    organizationName: displayName,
    inn: displayInn,
    address: organizationAddress,
    labExams: filteredRegularLabs,
    weldedJointExams: filteredWeldedJointLabs,
    certificates: filteredCerts,
    voluntaryCertificates: filteredVoluntaryCerts,
    declarationsByBasis,
  }));

  const handleOpenCertDetails = useCallback((cert: any) => {
    if (!cert.cert_reg_number) {
      return;
    }
    const encodedNumber = encodeURIComponent(cert.cert_reg_number);
    const certType = cert._cert_type;
    if (certType === 'Добровольная') {
      navigate(`/voluntary-certificates/${encodedNumber}`, { state: { cert } });
    } else {
      navigate(`/certificates/${encodedNumber}`, { state: { cert } });
    }
  }, [navigate]);

  const examResultsCount = useMemo(() => {
    const map = new Map();
    filteredRegularLabs.forEach((lab) => {
      if (lab.exam_result) {
        map.set(lab.exam_result, (map.get(lab.exam_result) || 0) + 1);
      }
    });
    return map;
  }, [filteredRegularLabs]);
  const examResultsPieData = useMemo(() => {
    const resultKeys = Array.from(examResultsCount.keys());
    const getBackgroundColors = () => {
      return resultKeys.map((result, idx) => {
        const resultColors = EXAM_RESULT_COLORS.resultColors as Record<string, string>;
        if (resultColors[result]) {
          return resultColors[result];
        } else {
          return EXAM_RESULT_COLORS.base[idx % EXAM_RESULT_COLORS.base.length];
        }
      });
    };

    return resultKeys.map((label, idx) => ({
      id: label,
      label: label === 'Не соответствует (фальсификат)' || label === 'Не соответствует НД (фальсификат)'
        ? 'Не соответствует\n(фальсификат)'
        : label,
      value: Array.from(examResultsCount.values())[idx],
      color: getBackgroundColors()[idx] || '#ccc',
    })).filter(item => item.value > 0);
  }, [examResultsCount]);

  const weldedExamResultsPieData = useMemo(() => {
    const counts = new Map<string, number>();
    filteredWeldedJointLabs.forEach(lab => {
      const result = lab.exam_result?.trim() || 'Нет данных';
      counts.set(result, (counts.get(result) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([label, value], index) => {
      const colors = EXAM_RESULT_COLORS.resultColors as Record<string, string>;
      return {
        id: label,
        label,
        value,
        color: colors[label] || EXAM_RESULT_COLORS.base[index % EXAM_RESULT_COLORS.base.length],
      };
    });
  }, [filteredWeldedJointLabs]);


  const certAptsCount = useMemo(() => {
    const map = new Map();
    filteredCerts.forEach((cert) => {
      if (cert.comment_apts) {
        map.set(cert.comment_apts, (map.get(cert.comment_apts) || 0) + 1);
      }
    });
    return map;
  }, [filteredCerts]);
  const certAptsPieData = useMemo(() => {
    const resultKeys = Array.from(certAptsCount.keys());

    const getColorForComment = (comment: string) => {
      switch (comment.toLowerCase()) {
        case 'нарушений нет':
          return 'rgba(126, 178, 70, 1)';
        case 'есть нарушения':
          return 'rgba(237, 93, 98, 1)';
        case 'обратить внимание':
          return 'rgba(255, 166, 0, 1)';
        default:
          return 'rgba(196, 196, 196, 1)';
      }
    };

    return resultKeys.map((label, idx) => {
      const color = getColorForComment(label);
      return {
        id: label,
        label,
        value: Array.from(certAptsCount.values())[idx],
        color,
      };
    }).filter(item => item.value > 0);
  }, [certAptsCount]);

  const voluntaryCertAptsCount = useMemo(() => {
    const map = new Map();
    filteredVoluntaryCerts.forEach((cert) => {
      if ((cert as any).comment_apts) {
        map.set((cert as any).comment_apts, (map.get((cert as any).comment_apts) || 0) + 1);
      }
    });
    return map;
  }, [filteredVoluntaryCerts]);
  const voluntaryCertAptsPieData = useMemo(() => {
    const resultKeys = Array.from(voluntaryCertAptsCount.keys());
    const getColorForComment = (comment: string) => {
      switch (comment.toLowerCase()) {
        case 'нарушений нет':
          return 'rgba(126, 178, 70, 1)';
        case 'есть нарушения':
          return 'rgba(237, 93, 98, 1)';
        case 'обратить внимание':
          return 'rgba(255, 166, 0, 1)';
        default:
          return 'rgba(196, 196, 196, 1)';
      }
    };
    return resultKeys.map((label, idx) => {
      const color = getColorForComment(label);
      return {
        id: label,
        label,
        value: Array.from(voluntaryCertAptsCount.values())[idx],
        color,
      };
    }).filter(item => item.value > 0);
  }, [voluntaryCertAptsCount]);

  // Проверка наличия данных для отрисовки диаграмм
  const hasRegularLabExamData = filteredRegularLabs.length > 0;
  const hasWeldedJointData = filteredWeldedJointLabs.length > 0;
  const hasWeldedJointDynamicsData = filteredWeldedJointLabs.some(item =>
    /^\d{4}$/.test(String(item.exam_date || '').substring(0, 4))
  );
  const hasCertData = filteredCerts.length > 0;
  const hasVoluntaryCertData = filteredVoluntaryCerts.length > 0;

  const hasExamResultsData = examResultsPieData.length > 0 &&
    examResultsPieData.some(item =>
      item.id.toLowerCase() !== 'нет данных' &&
      item.id !== 'Нет данных' &&
      item.value > 0
    );

  const hasCertAptsData = certAptsPieData.length > 0 &&
    certAptsPieData.some(item =>
      item.id.toLowerCase() !== 'нет данных' &&
      item.id !== 'Нет данных' &&
      item.value > 0
    );

  const hasVoluntaryCertAptsData = voluntaryCertAptsPieData.length > 0 &&
    voluntaryCertAptsPieData.some(item =>
      item.id.toLowerCase() !== 'нет данных' &&
      item.id !== 'Нет данных' &&
      item.value > 0
    );

  const getDynamicsData = () => {
    const years = ['2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'];

    const dataByYear = years.map(year => {
      const yearCertificates = filteredCerts.filter(cert => {
        const certYear = cert.cert_reg_data ? cert.cert_reg_data.substring(0, 4) : '';
        return certYear === year;
      });

      return {
        year,
        noViolations: yearCertificates.filter(cert => cert.comment_apts === "нарушений нет").length,
        hasViolations: yearCertificates.filter(cert => cert.comment_apts === "есть нарушения").length,
        attention: yearCertificates.filter(cert => cert.comment_apts === "обратить внимание").length
      };
    });

    return {
      labels: years,
      datasets: [
        {
          label: 'Нарушений нет',
          data: dataByYear.map(item => item.noViolations),
          backgroundColor: 'rgba(126, 178, 70, 1)',
          borderWidth: 1
        },
        {
          label: 'Есть нарушения',
          data: dataByYear.map(item => item.hasViolations),
          backgroundColor: 'rgba(237, 93, 98, 1)',
          borderWidth: 1
        },
        {
          label: 'Обратить внимание',
          data: dataByYear.map(item => item.attention),
          backgroundColor: 'rgba(255, 166, 0, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  // Проверка данных для динамики сертов
  const hasCertDynamicsData = useMemo(() => {
    const dynamicsData = getDynamicsData();
    return dynamicsData.datasets.some(dataset =>
      dataset.data.some(value => value > 0)
    );
  }, [filteredCerts]);

  const hasVoluntaryCertDynamicsData = useMemo(() => {
    const years = ['2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'];
    const dataByYear = years.map(year => {
      const yearCertificates = filteredVoluntaryCerts.filter(cert => {
        const certYear = cert.cert_reg_data ? cert.cert_reg_data.substring(0, 4) : '';
        return certYear === year;
      });
      return {
        year,
        noViolations: yearCertificates.filter(cert => cert.comment_apts === "нарушений нет").length,
        hasViolations: yearCertificates.filter(cert => cert.comment_apts === "есть нарушения").length,
        attention: yearCertificates.filter(cert => cert.comment_apts === "обратить внимание").length
      };
    });
    return dataByYear.some(item =>
      item.noViolations > 0 || item.hasViolations > 0 || item.attention > 0
    );
  }, [filteredVoluntaryCerts]);

  // Проверка данных для динамики лабораторных испытаний
  const hasLabExamDynamicsData = useMemo(() => {
    if (!hasRegularLabExamData) return false;

    const years = new Set<string>();
    filteredRegularLabs.forEach(item => {
      const year = item.exam_date ? String(item.exam_date).substring(0, 4) : '';
      if (year && /^\d{4}$/.test(year)) years.add(year);
    });

    const examResults = new Set<string>();
    filteredRegularLabs.forEach(item => {
      if (item.exam_result &&
          item.exam_result.toLowerCase() !== 'нет данных' &&
          item.exam_result !== 'Нет данных') {
        examResults.add(item.exam_result);
      }
    });

    return years.size > 0 && examResults.size > 0;
  }, [filteredRegularLabs, hasRegularLabExamData]);

  // Проверка кол-ва блоков для объединения страниц(если есть все блоки,то пдф по строгой логике)
  const remainingBlocksCount = useMemo(() => {
    let count = 0;
    if (hasLabExamDynamicsData) count++;
    if (hasCertDynamicsData) count++;
    if (hasVoluntaryCertDynamicsData) count++;
    if (hasRegularLabExamData) count++;
    if (hasWeldedJointData) count++;
    if (hasCertData || hasVoluntaryCertData) count++;
    return count;
  }, [hasLabExamDynamicsData, hasCertDynamicsData, hasVoluntaryCertDynamicsData, hasRegularLabExamData, hasWeldedJointData, hasCertData, hasVoluntaryCertData]);

  // Кол-во таблиц
  const tablesCount = useMemo(() => {
    let count = 0;
    if (hasRegularLabExamData) count++;
    if (hasWeldedJointData) count++;
    if (hasCertData || hasVoluntaryCertData) count++;
    return count;
  }, [hasRegularLabExamData, hasWeldedJointData, hasCertData, hasVoluntaryCertData]);

  // Проверка для объединения страниц
  // Если есть только диаграммы динамики (без таблиц) - объединяем
  // Если есть и диаграммы, и таблицы, но общее количество блоков <= 3 - объединяем
  // Если есть только таблицы (без диаграмм), но их <= 2 - объединяем
  const hasOnlyDynamicsCharts = (hasLabExamDynamicsData || hasCertDynamicsData || hasVoluntaryCertDynamicsData) &&
    !hasRegularLabExamData && !hasWeldedJointData && !(hasCertData || hasVoluntaryCertData);

  const hasOnlyTables = tablesCount > 0 &&
    !hasLabExamDynamicsData && !hasCertDynamicsData && !hasVoluntaryCertDynamicsData;

  // Проверка наличия диаграмм
  const hasAnyCharts = hasExamResultsData || hasCertAptsData || hasVoluntaryCertAptsData ||
    hasLabExamDynamicsData || hasCertDynamicsData || hasVoluntaryCertDynamicsData;

  const shouldCombinePages = hasOnlyDynamicsCharts ||
    remainingBlocksCount <= 3 ||
    (hasOnlyTables && tablesCount <= 2);

  // Отрисовка таблиц на отдельной странице при наличии диаграмм
  const shouldSeparateTablesFromCharts = hasAnyCharts;


  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 9999
    }}>
      <img
        src="/loading.png"
        alt="Загрузка..."
        style={{ width: 200, height: 200, animation: 'spin 1.2s linear infinite' }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  console.log('pieLabData', examResultsPieData);
  console.log('pieCertData', certAptsPieData);

  return (
    <div style={{ padding: 24 }}>
      <Header />
      <div ref={contentRef}>
        <nav className="breadcrumbs">
          <span
            className="breadcrumb-home"
            style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
            onClick={() => navigate('/')}
          >
            Главная страница
          </span>
          <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
          <span
            className="breadcrumb-orgform"
            style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
            onClick={() => navigate('/org-form')}
          >
            Комплексная проверка организации
          </span>
          <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
          <span className="breadcrumb-current" style={{ fontWeight: 500, color: '#000' }}>
            {name}
          </span>
        </nav>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div></div>
          <button className="download-button" onClick={handleDownload} disabled={pdfProgress !== null}>
            {getPdfButtonLabel(pdfProgress)}
          </button>
        </div>
        {pdfReport}
        <div className="pdf-page" style={{ pageBreakAfter: shouldCombinePages ? 'auto' : 'always' }}>
          <div className="examination-top-row" style={{ display: 'flex', gap: 24, marginBottom: 32 }}>
            <div className="org-info-container org-info-pdf">
              <h3>Информация об организации</h3>
              <div style={{ marginBottom: 10 }}><b>ИНН:</b> {displayInn}</div>
              <div style={{ marginBottom: 10 }}><b>Наименование:</b> {displayName}</div>
              <div><b>Адрес:</b> {organizationAddress}</div>
            </div>
            {hasExamResultsData && (
              <div className="pie-lab-container">
                <h3>Лабораторные испытания (результаты)</h3>
                <div style={{ width: 450, height: 260 }}>
                  <ResponsivePie
                    data={examResultsPieData}
                    margin={{ top: 20, right: 270, bottom: 20, left: 20 }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={16}
                    colors={examResultsPieData.map(d => d.color)}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    enableArcLabels={true}
                    arcLabel={d => String(d.value)}
                    enableArcLinkLabels={false}
                    theme={{
                      labels: { text: { fontWeight: 700, fill: '#111' } },
                    }}
                    legends={[{
                      anchor: 'right',
                      direction: 'column',
                      justify: false,
                      translateX: 150,
                      translateY: 0,
                      itemsSpacing: 4,
                      itemWidth: 90,
                      itemHeight: 18,
                      itemTextColor: '#999',
                      itemDirection: 'left-to-right',
                      itemOpacity: 1,
                      symbolSize: 15,
                      symbolShape: 'circle',
                      effects: [
                        { on: 'hover', style: { itemTextColor: '#000' } },
                      ],
                    }]}
                    tooltip={({ datum }) => {
                      const total = examResultsPieData.reduce((sum, d) => sum + d.value, 0);
                      const percent = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                      return (
                        <div style={{ padding: 8, background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}>
                          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{datum.label}</div>
                          <span style={{ color: datum.color, fontWeight: 700, marginRight: 8 }}>■</span>
                          <b>{datum.value}</b> <span style={{ color: '#888', marginLeft: 8 }}>({percent}%)</span>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            )}
            {(hasCertData || hasVoluntaryCertData) && (
              <div className="cert-container cert-container" style={{ flex: 1 }}>
                <h3>Сертификаты</h3>
                <div className="cert-total-summary">
                  <div className="cert-total-summary__item">
                    <span className="cert-total-summary__label">Общее количество:</span>
                    <span className="cert-total-summary__value">{filteredCerts.length + filteredVoluntaryCerts.length}</span>
                  </div>
                  <div className="cert-total-summary__item">
                    <span className="cert-total-summary__label">Обязательные:</span>
                    <span className="cert-total-summary__value">{filteredCerts.length}</span>
                  </div>
                  <div className="cert-total-summary__item">
                    <span className="cert-total-summary__label">Добровольные:</span>
                    <span className="cert-total-summary__value">{filteredVoluntaryCerts.length}</span>
                  </div>
                </div>
              </div>
            )}
            {hasWeldedJointData && (
              <div className="cert-container" style={{ flex: 1 }}>
                <h3>Испытания сварных соединений</h3>
                <div className="cert-total-summary">
                  <div className="cert-total-summary__item">
                    <span className="cert-total-summary__label">Общее количество:</span>
                    <span className="cert-total-summary__value">{filteredWeldedJointLabs.length}</span>
                  </div>
                  {weldedJointTypeCounts.map(([type, count]) => (
                    <div key={type} className="cert-total-summary__item">
                      <span className="cert-total-summary__label">{type}:</span>
                      <span className="cert-total-summary__value">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {hasDeclarationData && (
              <div className="cert-container" style={{ flex: 1 }}>
                <h3>Декларации</h3>
                <div className="cert-total-summary">
                  <div className="cert-total-summary__item">
                    <span className="cert-total-summary__label">Общее количество:</span>
                    <span className="cert-total-summary__value">
                      {declarationGroups.reduce((sum, group) => sum + group.basisDeclarations.length, 0)}
                    </span>
                  </div>
                  {declarationsByBasis.map(({ basis, basisDeclarations }) => (
                    <div key={basis} className="cert-total-summary__item">
                      <span className="cert-total-summary__label">{getLegalBasis(basis)}:</span>
                      <span className="cert-total-summary__value">{basisDeclarations.length}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="examination-results-grid">
          <div className="examination-results-grid__contents">
            {hasCertAptsData && (
              <div className="pie-cert-obligatory chart-container" style={{ flex: 1 }}>
                <h3>Обязательные сертификаты (АПТС)</h3>
                <div className="pie-chart">
                  <div className="pie-chart-inner" style={{ height: 270 }}>
                  <ResponsivePie
                    data={certAptsPieData}
                    margin={{ top: 20, right: 200, bottom: 20, left: 20 }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={16}
                    colors={certAptsPieData.map(d => d.color)}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    enableArcLabels={true}
                    arcLabel={d => String(d.value)}
                    enableArcLinkLabels={false}
                    theme={{ labels: { text: { fontWeight: 700, fill: '#111' } } }}
                    legends={[{ anchor: 'right', direction: 'column', translateX: 130, itemWidth: 90, itemHeight: 18 }]}
                  />
                  </div>
                </div>
              </div>
            )}
            {hasVoluntaryCertAptsData && (
              <div className="pie-cert-voluntary chart-container" style={{ flex: 1 }}>
                <h3>Добровольные сертификаты (АПТС)</h3>
                <div className="pie-chart">
                  <div className="pie-chart-inner" style={{ height: 270 }}>
                  <ResponsivePie
                    data={voluntaryCertAptsPieData}
                    margin={{ top: 20, right: 200, bottom: 20, left: 20 }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={16}
                    colors={voluntaryCertAptsPieData.map(d => d.color)}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    enableArcLabels={true}
                    arcLabel={d => String(d.value)}
                    enableArcLinkLabels={false}
                    theme={{ labels: { text: { fontWeight: 700, fill: '#111' } } }}
                    legends={[{ anchor: 'right', direction: 'column', translateX: 130, itemWidth: 90, itemHeight: 18 }]}
                  />
                  </div>
                </div>
              </div>
            )}
          </div>
          {hasWeldedJointData && (
            <div className="welded-joint-report-charts">
              <section className="welded-joint-report-chart">
                <h3>Испытания сварных соединений (результаты)</h3>
                <div className="welded-joint-report-pie">
                  <ResponsivePie
                    data={weldedExamResultsPieData}
                    margin={{ top: 18, right: 20, bottom: 54, left: 20 }}
                    innerRadius={0.58}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={8}
                    colors={weldedExamResultsPieData.map(item => item.color)}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    enableArcLabels={false}
                    enableArcLinkLabels={false}
                    legends={[{
                      anchor: 'bottom',
                      direction: 'row',
                      translateY: 48,
                      itemsSpacing: 10,
                      itemWidth: 170,
                      itemHeight: 18,
                      itemTextColor: '#555',
                      symbolSize: 12,
                      symbolShape: 'circle',
                    }]}
                    tooltip={({ datum }) => (
                      <div className="welded-joint-report-tooltip">
                        <span style={{ color: datum.color }}>●</span> {datum.label}: <b>{datum.value}</b>
                      </div>
                    )}
                  />
                  <div className="welded-joint-report-pie-total">{filteredWeldedJointLabs.length}</div>
                </div>
                <p className="welded-joint-report-disclaimer">
                  Результаты испытаний не характеризуют исключительно качество продукции, так как сварка - сложный комплексный процесс, и её результаты зависят от многих факторов.
                </p>
              </section>
              {hasWeldedJointDynamicsData && (
                <section className="welded-joint-report-chart">
                  <h3>Динамика испытаний по годам</h3>
                  <div className="welded-joint-report-bar">
                    <BarChartLabExam
                      data={filteredWeldedJointLabs}
                      filteredYears={undefined}
                      onBarClick={() => { }}
                      compact
                    />
                  </div>
                </section>
              )}
            </div>
          )}
          {hasLabExamDynamicsData && (
            <div className={`bar-labexam-container examination-results-grid__lab-dynamics${hasWeldedJointData ? '' : ' examination-results-grid__lab-dynamics--full-width'}`}>
              <h3>Динамика лабораторных испытаний по годам</h3>
              <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <BarChartLabExam
                  data={filteredRegularLabs}
                  filteredYears={undefined}
                  onBarClick={() => { }}
                  compact
                />
              </div>
            </div>
          )}
          </div>
          {/* Если данных мало и нет диаграмм, то таблицы переходят первую страницу */}
          {shouldCombinePages && !shouldSeparateTablesFromCharts && hasRegularLabExamData && (
            <div className="tables-container" style={{ marginTop: 32 }}>
              <div className="table-block">
                <h3>Лабораторные испытания</h3>
                <DetailInfo currentItems={filteredRegularLabs} sortField={null} sortDirection={null} onSort={() => { }} />
              </div>
            </div>
          )}
          {shouldCombinePages && !shouldSeparateTablesFromCharts && hasWeldedJointData && (
            <div className="tables-container" style={{ marginTop: 32 }}>
              <div className="table-block">
                <h3>Испытания сварных соединений</h3>
                <DetailInfo currentItems={filteredWeldedJointLabs} sortField={null} sortDirection={null} onSort={() => { }} weldedJointsOnly />
              </div>
            </div>
          )}
          {shouldCombinePages && !shouldSeparateTablesFromCharts && (hasCertData || hasVoluntaryCertData) && (
            <div className="tables-container" style={{ marginTop: 32 }}>
              <div className="table-block">
                <h3>Сертификаты</h3>
                <Certificates
                  filteredLabList={[
                    ...filteredCerts.map((c) => ({ ...(c as any), _cert_type: 'Обязательная' })),
                    ...filteredVoluntaryCerts.map((c) => ({ ...(c as any), _cert_type: 'Добровольная' })),
                  ] as any}
                  onSort={() => { }}
                  getSortIcon={() => ''}
                  showTypeColumn
                  onRowClick={handleOpenCertDetails}
                />
              </div>
            </div>
          )}
        </div>

        {shouldCombinePages ? (
          // Объединение всех блоков на одну страницу
          <div className="pdf-page">
            {hasCertDynamicsData && (
              <div className="bar-dynamics-container" style={{ marginBottom: 32 }}>
                <h3>Динамика обязательных сертификатов по годам</h3>
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  <BarChartDynamics
                    certif={filteredCerts}
                    filteredAptsComments={[]}
                    filteredCertStatuses={[]}
                    filters={{
                      cert_reg_number: [],
                      os_name: [],
                      os_number: [],
                      applicant_shortname: [],
                      manufacture_inn: [],
                      cert_reg_data: [],
                      manufacture_fullname: [],
                      applicant_inn: [],
                      applicant_region: [],
                      region_manufacturer: [],
                    }}
                    filteredYears={undefined}
                    onBarClick={() => { }}
                  />
                </div>
              </div>
            )}
            {hasVoluntaryCertDynamicsData && (
              <div className="bar-dynamics-container" style={{ marginBottom: 32 }}>
                <h3>Динамика добровольных сертификатов по годам</h3>
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  <BarChartDynamics
                    certif={filteredVoluntaryCerts as any}
                    filteredAptsComments={[]}
                    filteredCertStatuses={[]}
                    filters={{
                      cert_reg_number: [],
                      os_name: [],
                      os_number: [],
                      applicant_shortname: [],
                      manufacture_inn: [],
                      cert_reg_data: [],
                      manufacture_fullname: [],
                      applicant_inn: [],
                      applicant_region: [],
                      region_manufacturer: [],
                    }}
                    filteredYears={undefined}
                    onBarClick={() => { }}
                  />
                </div>
              </div>
            )}
            {renderDeclarationDynamics({ marginBottom: 32 })}
            {/* Если есть диаграммы, то таблицы переходят на отдельную страницу */}
            {shouldSeparateTablesFromCharts && hasRegularLabExamData && (
              <div className="tables-container" style={{ marginBottom: 32 }}>
                <div className="table-block">
                  <h3>Лабораторные испытания</h3>
                  <DetailInfo currentItems={filteredRegularLabs} sortField={null} sortDirection={null} onSort={() => { }} />
                </div>
              </div>
            )}
            {shouldSeparateTablesFromCharts && hasWeldedJointData && (
              <div className="tables-container" style={{ marginBottom: 32 }}>
                <div className="table-block">
                  <h3>Испытания сварных соединений</h3>
                  <DetailInfo currentItems={filteredWeldedJointLabs} sortField={null} sortDirection={null} onSort={() => { }} weldedJointsOnly />
                </div>
              </div>
            )}
            {shouldSeparateTablesFromCharts && (hasCertData || hasVoluntaryCertData) && (
              <div className="tables-container">
                <div className="table-block">
                  <h3>Сертификаты</h3>
                  <Certificates
                    filteredLabList={[
                      ...filteredCerts.map((c) => ({ ...(c as any), _cert_type: 'Обязательная' })),
                      ...filteredVoluntaryCerts.map((c) => ({ ...(c as any), _cert_type: 'Добровольная' })),
                    ] as any}
                    onSort={() => { }}
                    getSortIcon={() => ''}
                    showTypeColumn
                    onRowClick={handleOpenCertDetails}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          // Разделение блоков по отдельным страницам
          <>
            <div className="pdf-page" style={{ pageBreakAfter: 'always' }}>
              {hasCertDynamicsData && (
                <div className="bar-dynamics-container">
                  <h3>Динамика обязательных сертификатов по годам</h3>
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <BarChartDynamics
                      certif={filteredCerts}
                      filteredAptsComments={[]}
                      filteredCertStatuses={[]}
                      filters={{
                        cert_reg_number: [],
                        os_name: [],
                        os_number: [],
                        applicant_shortname: [],
                        manufacture_inn: [],
                        cert_reg_data: [],
                        manufacture_fullname: [],
                        applicant_inn: [],
                        applicant_region: [],
                        region_manufacturer: [],
                      }}
                      filteredYears={undefined}
                      onBarClick={() => { }}
                    />
                  </div>
                </div>
              )}
              {hasVoluntaryCertDynamicsData && (
                <div className="bar-dynamics-container" style={{ marginTop: 24 }}>
                  <h3>Динамика добровольных сертификатов по годам</h3>
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <BarChartDynamics
                      certif={filteredVoluntaryCerts as any}
                      filteredAptsComments={[]}
                      filteredCertStatuses={[]}
                      filters={{
                        cert_reg_number: [],
                        os_name: [],
                        os_number: [],
                        applicant_shortname: [],
                        manufacture_inn: [],
                        cert_reg_data: [],
                        manufacture_fullname: [],
                        applicant_inn: [],
                        applicant_region: [],
                        region_manufacturer: [],
                      }}
                      filteredYears={undefined}
                      onBarClick={() => { }}
                    />
                  </div>
                </div>
              )}
              {renderDeclarationDynamics({ marginTop: 24 })}
            </div>

            {hasRegularLabExamData && (
              <div className="pdf-page" style={{ pageBreakAfter: tablesCount <= 2 ? 'auto' : 'always' }}>
                <div className="tables-container">
                  <div className="table-block">
                    <h3>Лабораторные испытания</h3>
                    <DetailInfo currentItems={filteredRegularLabs} sortField={null} sortDirection={null} onSort={() => { }} />
                  </div>
                </div>
              </div>
            )}
            {hasWeldedJointData && (
              <div className="pdf-page" style={{ pageBreakAfter: tablesCount <= 2 ? 'auto' : 'always' }}>
                <div className="tables-container">
                  <div className="table-block">
                    <h3>Испытания сварных соединений</h3>
                    <DetailInfo currentItems={filteredWeldedJointLabs} sortField={null} sortDirection={null} onSort={() => { }} weldedJointsOnly />
                  </div>
                </div>
              </div>
            )}
            {(hasCertData || hasVoluntaryCertData) && (
              <div className="pdf-page">
                <div className="tables-container">
                  <div className="table-block">
                    <h3>Сертификаты</h3>
                    <Certificates
                      filteredLabList={[
                        ...filteredCerts.map((c) => ({ ...(c as any), _cert_type: 'Обязательная' })),
                        ...filteredVoluntaryCerts.map((c) => ({ ...(c as any), _cert_type: 'Добровольная' })),
                      ] as any}
                      onSort={() => { }}
                      getSortIcon={() => ''}
                      showTypeColumn
                      onRowClick={handleOpenCertDetails}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {(declarationsLoading || declarationsError || declarations.length > 0) && (
          <div className="pdf-page" style={{ marginTop: 32 }}>
            {declarationsLoading && (
              <div style={{ color: '#888', padding: '16px 0' }}>Загрузка деклараций...</div>
            )}
            {declarationsError && (
              <div style={{ color: '#888', padding: '16px 0' }}>
                Не удалось загрузить декларации ПП 2425 и ПП 353. Остальные данные проверки актуальны.
              </div>
            )}
            {!declarationsLoading && declarationGroups.map(({ basis, basisDeclarations }) => (
              <div key={basis} style={{ marginBottom: 32 }}>
                <div className="tables-container">
                  <div className="table-block">
                    <h3>Декларации {getLegalBasis(basis)}</h3>
                    <DeclarationsTable
                      declarations={basisDeclarations}
                      exportFileName={`Декларации ${getLegalBasis(basis)} ${displayInn}`}
                      onRowClick={(declaration) => handleOpenDeclarationDetails(basis, declaration)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Examination;
